import { useCallback } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { useApi } from "./useApi";
import { useSessionSetAtom } from "../useSessionSetAtom";
import type {
    OptionResponse,
    CreateOptionRequest,
    CreateOptionResponse,
    UpdateOptionRequest,
} from "@models/option";
import {
    optionsAtom,
    isFetchingOptionsAtom,
    isProcessingOptionsAtom,
} from "@/store/optionAtoms";

const OPTIONS_ENDPOINT = "/options";

export const useOptionApi = () => {
    const { callApi } = useApi();
    // Session-bound: an answer landing after sign-out must not refill it.
    const options = useAtomValue(optionsAtom);
    const setOptions = useSessionSetAtom(optionsAtom);
    const setIsFetchingOptions = useSetAtom(isFetchingOptionsAtom);
    const setIsProcessingOptions = useSetAtom(isProcessingOptionsAtom);

    // `skipErrorNotification` is opt-in: for a caller that renders this read's
    // failure inline (the Manage Options modal's open read). Left out,
    // callApi's own default applies and the toast stays.
    const getOptions = useCallback(
        async ({
            skipErrorNotification,
        }: { skipErrorNotification?: boolean } = {}): Promise<
            OptionResponse[]
        > => {
            setIsFetchingOptions(true);
            try {
                const data = await callApi<OptionResponse[]>(OPTIONS_ENDPOINT, {
                    skipErrorNotification,
                });
                setOptions(data);
                return data;
            } finally {
                setIsFetchingOptions(false);
            }
        },
        [callApi, setOptions, setIsFetchingOptions],
    );

    const getOption = useCallback(
        async (optionId: number): Promise<OptionResponse> => {
            setIsFetchingOptions(true);
            try {
                return await callApi<OptionResponse>(`${OPTIONS_ENDPOINT}/${optionId}`);
            } finally {
                setIsFetchingOptions(false);
            }
        },
        [callApi, setIsFetchingOptions],
    );

    const createOption = useCallback(
        async (data: CreateOptionRequest): Promise<CreateOptionResponse> => {
            setIsProcessingOptions(true);
            try {
                const response = await callApi<CreateOptionResponse>(OPTIONS_ENDPOINT, {
                    method: "POST",
                    body: JSON.stringify(data),
                });
                const newOption: OptionResponse = {
                    id: response.id,
                    name: data.name,
                    isActive: true,
                    values: [],
                };
                setOptions([...options, newOption]);
                return response;
            } finally {
                setIsProcessingOptions(false);
            }
        },
        [callApi, setIsProcessingOptions, options, setOptions],
    );

    const updateOption = useCallback(
        async (optionId: number, data: UpdateOptionRequest): Promise<void> => {
            setIsProcessingOptions(true);
            try {
                await callApi(`${OPTIONS_ENDPOINT}/${optionId}`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                });
            } finally {
                setIsProcessingOptions(false);
            }
        },
        [callApi, setIsProcessingOptions],
    );

    const deleteOption = useCallback(
        async (optionId: number): Promise<void> => {
            setIsProcessingOptions(true);
            try {
                await callApi(`${OPTIONS_ENDPOINT}/${optionId}`, { method: "DELETE" });
                setOptions(options.filter((o) => o.id !== optionId));
            } finally {
                setIsProcessingOptions(false);
            }
        },
        [callApi, setIsProcessingOptions, options, setOptions],
    );

    return {
        getOptions,
        getOption,
        createOption,
        updateOption,
        deleteOption,
    };
};
